import { useState, type ReactNode } from 'react';
import {
  Alert,
  type AlertColor,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import SettingsIcon from '@mui/icons-material/Settings';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import {
  useAdminAtualizarIdentificadorMutation,
  useAdminCriarIdentificadorMutation,
  useAdminCriarIdentificadoresEmFaixaMutation,
  useAdminListarIdentificadoresQuery,
  useAdminRemoverIdentificadorMutation,
  useLazyAdminExportarIdentificadoresQuery,
  type IdentificadorLoja,
  type TipoIdentificador,
} from '../../api';
import { dominioCanonico, montarUrlIdentificador } from '../../lib/url-identificador';
import { baixarQrCodesZip } from '../../lib/exportar-qrcodes';
import { DialogQrCode } from './dialog-qrcode';
import { DialogParearDispositivo } from './dialog-parear-dispositivo';
import { ehTipoPareavel } from '../../lib/tipos-atendimento';
import { IconeTipoIdentificador, ROTULO_TIPO_IDENTIFICADOR } from './icone-modo';
import { PaginacaoTabela, useUltimaPagina } from './paginacao-tabela';

/** Ordem em que os tipos aparecem no select de criação (a mesma das abas). */
const TODOS_OS_TIPOS: TipoIdentificador[] = [
  'MESA',
  'COMANDA',
  'TOTEM',
  'TOTEM_CHECKOUT',
  'TABLET',
  'DELIVERY',
  'VITRINE',
];

/** Rótulo do tipo no select de criação — mais explícito que o rótulo curto. */
const ROTULO_TIPO_NO_SELECT: Record<TipoIdentificador, string> = {
  MESA: 'Mesa',
  COMANDA: 'Comanda',
  TOTEM: 'Totem (autoatendimento)',
  TOTEM_CHECKOUT: 'Totem Checkout',
  TABLET: 'Tablet (na mesa)',
  DELIVERY: 'Delivery',
  VITRINE: 'Vitrine',
};

function ehTipoPublico(tipo: TipoIdentificador): boolean {
  return tipo === 'DELIVERY' || tipo === 'VITRINE';
}

/** Uma entrada do menu "Baixar QR codes". */
interface OpcaoDownload {
  rotulo: string;
  tipos: TipoIdentificador[];
  zip: string;
}

/**
 * Opções de download dentro do escopo da tela.
 *
 * Só existe para quem IMPRIME o QR: mesa, comanda, delivery e vitrine. Totem,
 * totem checkout e tablet são aparelhos fixos — lá o endereço é aberto uma vez
 * no navegador do equipamento, não vira etiqueta para a gráfica.
 */
function opcoesDownload(escopo: TipoIdentificador[]): { locais: OpcaoDownload[]; publicas: OpcaoDownload[] } {
  const tem = (t: TipoIdentificador): boolean => escopo.includes(t);
  const locais: OpcaoDownload[] = [];
  if (tem('MESA')) locais.push({ rotulo: 'QR codes das mesas', tipos: ['MESA'], zip: 'qrcodes-mesas.zip' });
  if (tem('COMANDA')) locais.push({ rotulo: 'QR codes das comandas', tipos: ['COMANDA'], zip: 'qrcodes-comandas.zip' });
  if (tem('MESA') && tem('COMANDA')) {
    locais.push({ rotulo: 'Mesas e comandas (tudo)', tipos: ['MESA', 'COMANDA'], zip: 'qrcodes-mesas-comandas.zip' });
  }

  const publicas: OpcaoDownload[] = [];
  if (tem('DELIVERY')) publicas.push({ rotulo: 'QR code do delivery', tipos: ['DELIVERY'], zip: 'qrcodes-delivery.zip' });
  if (tem('VITRINE')) publicas.push({ rotulo: 'QR code da vitrine', tipos: ['VITRINE'], zip: 'qrcodes-vitrine.zip' });

  return { locais, publicas };
}

interface EstadoEditor {
  modo: 'criar' | 'editar';
  /** Preenchido só no modo editar — usado pra `PATCH /admin/identificadores/:id`. */
  ident?: IdentificadorLoja;
  tipo: TipoIdentificador;
  numero: string;
  codigo: string;
  /** DELIVERY/VITRINE: code é um domínio completo (cardapiox.com.br). */
  dominioCompleto: boolean;
  /** Criação em FAIXA (só MESA/COMANDA no modo criar): cria de `numeroInicial` a `numeroFinal`. */
  faixa: boolean;
  numeroInicial: string;
  numeroFinal: string;
}

function editorCriar(tipo: TipoIdentificador): EstadoEditor {
  return {
    modo: 'criar',
    tipo,
    numero: '',
    codigo: '',
    dominioCompleto: false,
    faixa: false,
    numeroInicial: '',
    numeroFinal: '',
  };
}

interface GerenciadorPontosAcessoProps {
  idConfig: number;
  /**
   * Recorta a tela a estes tipos — listagem (filtro no servidor), criação
   * (select de tipo) e download dos QR. Omitido = a loja inteira, que é como a
   * tela "Acessos ao cardápio" do menu lateral funciona.
   */
  tipos?: TipoIdentificador[];
  /**
   * Onde entram os botões de ação ("Baixar QR codes" e "Novo ponto de acesso").
   * Existe porque os dois donos deste componente têm cabeçalhos diferentes: a
   * tela usa o `acoes` do HeaderTela, o modal usa a própria barra de título.
   * Sem ele, os botões saem numa barra à direita, acima da tabela.
   */
  cabecalho?: (acoes: ReactNode) => ReactNode;
  /** Abre o formulário de criação já na montagem (atalho "Criar ponto de acesso"). */
  criarAoMontar?: boolean;
  /** Itens por página inicial — no modal cabe menos que na tela cheia. */
  tamanhoPaginaInicial?: number;
}

/**
 * Listagem + CRUD dos pontos de acesso da loja. Mora aqui, e não na página,
 * porque tem dois donos: a tela do menu lateral (loja inteira) e o modal que o
 * "Gerenciar pontos de acesso" de cada aba de configuração abre, recortado ao
 * tipo daquela aba.
 *
 * O recorte por `tipos` não é só visual: filtra a consulta no servidor, limita
 * os tipos que o formulário oferece e some com o download de QR quando o tipo é
 * de aparelho fixo. Assim o modal da aba do totem não vira uma porta dos fundos
 * para criar uma mesa.
 */
export function GerenciadorPontosAcesso({
  idConfig,
  tipos,
  cabecalho,
  criarAoMontar = false,
  tamanhoPaginaInicial = 20,
}: GerenciadorPontosAcessoProps): JSX.Element {
  const filtrado = !!tipos?.length;
  const escopo = filtrado ? tipos! : TODOS_OS_TIPOS;
  const opcoesTipo = TODOS_OS_TIPOS.filter((t) => escopo.includes(t));
  const tipoPadrao = opcoesTipo[0] ?? 'MESA';

  const [pagina, setPagina] = useState(0);
  const [tamanhoPagina, setTamanhoPagina] = useState(tamanhoPaginaInicial);
  const { data, isLoading } = useAdminListarIdentificadoresQuery({
    idConfig,
    pagina: pagina + 1,
    tamanhoPagina,
    tipos: filtrado ? tipos : undefined,
  });
  const { itens, total } = useUltimaPagina(data);
  const [criar, statusCriar] = useAdminCriarIdentificadorMutation();
  const [criarFaixa, statusFaixa] = useAdminCriarIdentificadoresEmFaixaMutation();
  const [atualizar, statusAtualizar] = useAdminAtualizarIdentificadorMutation();
  const [remover] = useAdminRemoverIdentificadorMutation();

  const [editor, setEditor] = useState<EstadoEditor | null>(() => (criarAoMontar ? editorCriar(tipoPadrao) : null));
  const [erro, setErro] = useState<string | null>(null);
  // Aviso do rodapé. Carrega a severidade junto: recusa do Sischef (comanda que
  // não existe) não pode aparecer com a mesma cara de "deu certo".
  const [feedback, setFeedback] = useState<{ texto: string; tipo: AlertColor } | null>(null);

  function avisar(texto: string, tipo: AlertColor = 'success'): void {
    setFeedback({ texto, tipo });
  }

  const [verQrDe, setVerQrDe] = useState<IdentificadorLoja | null>(null);
  /** Ponto cujo código de pareamento está aberto — `null` = fechado. */
  const [parearDe, setParearDe] = useState<IdentificadorLoja | null>(null);

  // Download dos QR codes em ZIP (para gráfica).
  const [exportar] = useLazyAdminExportarIdentificadoresQuery();
  const [menuBaixar, setMenuBaixar] = useState<null | HTMLElement>(null);
  const [gerandoZip, setGerandoZip] = useState(false);

  const { locais: baixarLocais, publicas: baixarPublicas } = opcoesDownload(escopo);
  const opcoesBaixar = [...baixarLocais, ...baixarPublicas];
  /** Com uma opção só, o botão baixa direto — menu de um item é ruído. */
  const baixarDireto = opcoesBaixar.length === 1 ? opcoesBaixar[0] : null;

  async function baixarQrCodes(opcao: OpcaoDownload): Promise<void> {
    setMenuBaixar(null);
    setGerandoZip(true);
    try {
      const res = await exportar({ idConfig, tipos: opcao.tipos }).unwrap();
      if (res.itens.length === 0) {
        avisar('Nenhum ponto de acesso ativo desse tipo para gerar QR codes.', 'info');
        return;
      }
      await baixarQrCodesZip(res.itens, opcao.zip);
      avisar(
        res.itens.length >= res.limite
          ? `Baixados os primeiros ${res.limite} QR codes (limite). Baixe por tipo para pegar o restante.`
          : `${res.itens.length} QR code(s) baixado(s) em ${opcao.zip}.`,
      );
    } catch {
      avisar('Não foi possível gerar os QR codes. Tente novamente.', 'error');
    } finally {
      setGerandoZip(false);
    }
  }

  function abrirCriar(): void {
    setErro(null);
    setEditor(editorCriar(tipoPadrao));
  }

  function abrirEditar(ident: IdentificadorLoja): void {
    setErro(null);
    setEditor({
      modo: 'editar',
      ident,
      tipo: ident.tipo,
      numero: ident.numero ?? '',
      codigo: ident.codigo,
      dominioCompleto: ident.dominioCompleto ?? false,
      faixa: false,
      numeroInicial: '',
      numeroFinal: '',
    });
  }

  function fecharEditor(): void {
    setEditor(null);
    setErro(null);
  }

  async function confirmar(): Promise<void> {
    if (!editor) return;
    setErro(null);
    try {
      if (editor.modo === 'criar') {
        if ((editor.tipo === 'MESA' || editor.tipo === 'COMANDA') && editor.faixa) {
          // Cria a faixa inteira de uma vez (mesas/comandas de X a Y).
          const res = await criarFaixa({
            idConfiguracaoCardapio: idConfig,
            tipo: editor.tipo,
            numeroInicial: parseInt(editor.numeroInicial, 10),
            numeroFinal: parseInt(editor.numeroFinal, 10),
          }).unwrap();
          // Comanda sem cadastro no Sischef é pulada pelo backend — dizer QUAIS é o
          // que permite ao lojista cadastrar lá e refazer só a parte que faltou.
          const semCadastro = res.naoCadastradas ?? [];
          const detalhe = semCadastro.length
            ? ` — ${semCadastro.length} não cadastrada(s) no Sischef: ${semCadastro.slice(0, 10).join(', ')}${
                semCadastro.length > 10 ? '…' : ''
              }`
            : '';
          const repetidas = res.pulados - semCadastro.length;
          avisar(
            `${res.criados} acesso(s) criado(s)` +
              (repetidas > 0 ? ` — ${repetidas} já existia(m)` : '') +
              detalhe +
              '.',
            semCadastro.length > 0 ? 'warning' : 'success',
          );
        } else {
          await criar({
            idConfiguracaoCardapio: idConfig,
            tipo: editor.tipo,
            numero: ehTipoPublico(editor.tipo) ? undefined : editor.numero.trim() || undefined,
            codigo: ehTipoPublico(editor.tipo) ? editor.codigo.trim() : undefined,
            dominioCompleto: ehTipoPublico(editor.tipo) ? editor.dominioCompleto : undefined,
          }).unwrap();
        }
      } else {
        const id = editor.ident!.id;
        const dados: { numero?: string; codigo?: string; tipo?: string; dominioCompleto?: boolean } = {};
        if (ehTipoPublico(editor.tipo)) {
          // tipo + dominioCompleto vão junto p/ o backend validar o `codigo`
          // como slug ou domínio.
          dados.codigo = editor.codigo.trim();
          dados.tipo = editor.tipo;
          dados.dominioCompleto = editor.dominioCompleto;
        } else {
          dados.numero = editor.numero.trim();
        }
        await atualizar({ id, dados }).unwrap();
      }
      fecharEditor();
    } catch (err) {
      const msg = (err as { data?: { mensagem?: string } })?.data?.mensagem;
      setErro(msg ?? 'Falha ao salvar ponto de acesso');
    }
  }

  /**
   * Copia o link público do ponto de acesso.
   *
   * O endereço é derivado do identificador (`montarUrlIdentificador`), o mesmo
   * que o QR carrega — nada de montar a URL na mão em cada tela.
   */
  async function copiarLink(ident: IdentificadorLoja): Promise<void> {
    const url = montarUrlIdentificador(ident);
    try {
      await navigator.clipboard.writeText(url);
      avisar('Link copiado.');
    } catch {
      // Clipboard bloqueado (contexto sem HTTPS, permissão negada): mostrar a URL
      // ainda resolve — dá para selecionar e copiar à mão.
      avisar(url, 'info');
    }
  }

  // Lixeiro = inativar (soft-delete). Reativa via PATCH { ativo: true }. A lista é
  // invalidada pelas mutations, então ela reflete o novo status ao refetchar.
  async function alternarAtivo(ident: IdentificadorLoja): Promise<void> {
    try {
      if (ident.ativo) {
        await remover(ident.id).unwrap();
      } else {
        await atualizar({ id: ident.id, dados: { ativo: true } }).unwrap();
      }
    } catch (err) {
      // Reativar comanda que não existe mais no Sischef cai aqui: sem a mensagem,
      // o botão parecia não fazer nada.
      const msg = (err as { data?: { mensagem?: string } })?.data?.mensagem;
      avisar(msg ?? 'Não foi possível alterar o ponto de acesso.', 'error');
    }
  }

  const salvando = statusCriar.isLoading || statusFaixa.isLoading || statusAtualizar.isLoading;

  const acoes = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
      {opcoesBaixar.length > 0 && (
        <Button
          variant="outlined"
          startIcon={gerandoZip ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          onClick={(e) => (baixarDireto ? baixarQrCodes(baixarDireto) : setMenuBaixar(e.currentTarget))}
          disabled={gerandoZip}
          fullWidth
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Baixar QR codes
        </Button>
      )}
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={abrirCriar}
        fullWidth
        sx={{ width: { xs: '100%', sm: 'auto' } }}
      >
        Novo ponto de acesso
      </Button>
    </Stack>
  );

  return (
    <>
      {cabecalho ? (
        cabecalho(acoes)
      ) : (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          {acoes}
        </Stack>
      )}

      <Menu anchorEl={menuBaixar} open={!!menuBaixar} onClose={() => setMenuBaixar(null)}>
        {baixarLocais.map((o) => (
          <MenuItem key={o.zip} onClick={() => baixarQrCodes(o)}>
            {o.rotulo}
          </MenuItem>
        ))}
        {baixarLocais.length > 0 && baixarPublicas.length > 0 && <Divider />}
        {baixarPublicas.map((o) => (
          <MenuItem key={o.zip} onClick={() => baixarQrCodes(o)}>
            {o.rotulo}
          </MenuItem>
        ))}
      </Menu>

      <Paper variant="outlined">
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          // Rolagem horizontal em vez de espremer 5 colunas: no celular (e no
          // modal, mais estreito que a tela) a tabela não cabe inteira.
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Número</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itens.map((ident) => (
                  <TableRow key={ident.id}>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<IconeTipoIdentificador tipo={ident.tipo} />}
                        label={ROTULO_TIPO_IDENTIFICADOR[ident.tipo] ?? ident.tipo}
                      />
                    </TableCell>
                    <TableCell>{ident.numero ?? '—'}</TableCell>
                    <TableCell>
                      <code style={{ fontSize: 12 }}>{ident.codigo}</code>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={ident.ativo ? 'Ativo' : 'Inativo'}
                        color={ident.ativo ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {/* Só totem pareia, e só enquanto ativo. */}
                      {ident.ativo && ehTipoPareavel(ident.tipo) && (
                        <Tooltip title="Configurar totem">
                          <IconButton onClick={() => setParearDe(ident)}>
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Copiar link">
                        <IconButton onClick={() => void copiarLink(ident)}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Abrir em nova aba">
                        <IconButton
                          component="a"
                          href={montarUrlIdentificador(ident)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ver QR code">
                        <IconButton onClick={() => setVerQrDe(ident)}>
                          <QrCode2Icon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton onClick={() => abrirEditar(ident)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={ident.ativo ? 'Inativar' : 'Reativar'}>
                        <IconButton onClick={() => alternarAtivo(ident)} color={ident.ativo ? 'error' : 'success'}>
                          {ident.ativo ? <DeleteOutlineIcon /> : <CheckCircleOutlineIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {itens.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        {filtrado
                          ? 'Nenhum ponto de acesso deste tipo ainda.'
                          : 'Nenhum ponto de acesso cadastrado ainda.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
        <PaginacaoTabela
          total={total}
          pagina={pagina}
          tamanhoPagina={tamanhoPagina}
          aoMudarPagina={setPagina}
          aoMudarTamanho={(t) => {
            setTamanhoPagina(t);
            setPagina(0);
          }}
        />
      </Paper>

      <DialogEditor
        editor={editor}
        erro={erro}
        salvando={salvando}
        opcoesTipo={opcoesTipo}
        onChange={setEditor}
        onCancelar={fecharEditor}
        onConfirmar={confirmar}
      />

      <DialogQrCode identificador={verQrDe} aoFechar={() => setVerQrDe(null)} />

      <DialogParearDispositivo identificador={parearDe} aoFechar={() => setParearDe(null)} />

      <Snackbar
        open={!!feedback}
        autoHideDuration={4000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={feedback?.tipo ?? 'success'} variant="filled" onClose={() => setFeedback(null)}>
          {feedback?.texto ?? ''}
        </Alert>
      </Snackbar>
    </>
  );
}

// ====== Dialog reusável (criar/editar) ======

interface DialogEditorProps {
  editor: EstadoEditor | null;
  erro: string | null;
  salvando: boolean;
  /** Tipos que o select oferece — recortados quando a tela está filtrada. */
  opcoesTipo: TipoIdentificador[];
  onChange: (e: EstadoEditor) => void;
  onCancelar: () => void;
  onConfirmar: () => void;
}

function DialogEditor({
  editor,
  erro,
  salvando,
  opcoesTipo,
  onChange,
  onCancelar,
  onConfirmar,
}: DialogEditorProps): JSX.Element {
  const aberto = !!editor;
  const ehEditar = editor?.modo === 'editar';
  const tipo = editor?.tipo ?? 'MESA';
  const publico = ehTipoPublico(tipo);
  // TOTEM, TOTEM_CHECKOUT e TABLET são dispositivos da loja: o "número" é a
  // identificação do aparelho (a mesa do tablet é definida localmente no próprio aparelho).
  const ehDispositivo = tipo === 'TOTEM' || tipo === 'TOTEM_CHECKOUT' || tipo === 'TABLET';

  // Recortado a um tipo só (modal de uma aba), o select não tem escolha a
  // oferecer — fica travado, mostrando de que tipo é o ponto sendo criado.
  const tipoUnico = opcoesTipo.length === 1;

  // Criação em FAIXA: só MESA/COMANDA e só no modo criar.
  const podeFaixa = !ehEditar && (tipo === 'MESA' || tipo === 'COMANDA');
  const emFaixa = podeFaixa && !!editor?.faixa;
  const iniFaixa = parseInt(editor?.numeroInicial ?? '', 10);
  const fimFaixa = parseInt(editor?.numeroFinal ?? '', 10);
  const totalFaixa = Number.isInteger(iniFaixa) && Number.isInteger(fimFaixa) ? fimFaixa - iniFaixa + 1 : 0;
  const faixaValida = emFaixa && totalFaixa >= 1 && totalFaixa <= 50;

  // Validação simples antes de habilitar o botão Salvar.
  const podeSalvar = !!editor && (publico ? !!editor.codigo.trim() : emFaixa ? faixaValida : true);

  return (
    <Dialog open={aberto} onClose={onCancelar} fullWidth maxWidth="sm">
      <DialogTitle>
        {ehEditar ? 'Editar ponto de acesso' : emFaixa ? 'Nova faixa de acessos' : 'Novo ponto de acesso'}
      </DialogTitle>
      <DialogContent>
        {editor && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Tipo"
              value={editor.tipo}
              onChange={(e) => onChange({ ...editor, tipo: e.target.value as TipoIdentificador })}
              fullWidth
              disabled={ehEditar || tipoUnico}
              helperText={ehEditar ? 'O tipo não pode ser alterado depois de criado.' : undefined}
            >
              {opcoesTipo.map((t) => (
                <MenuItem key={t} value={t}>
                  {ROTULO_TIPO_NO_SELECT[t]}
                </MenuItem>
              ))}
            </TextField>

            {tipo === 'TABLET' && (
              <Alert severity="info">
                O <strong>número da mesa</strong> é definido no próprio tablet (protegido por PIN), não aqui. Este
                acesso é apenas o link que o aparelho abre — use um nome só para identificá-lo.
              </Alert>
            )}

            {/* MESA/COMANDA no modo criar: opção de criar vários de uma vez (faixa). */}
            {podeFaixa && (
              <FormControlLabel
                control={<Switch checked={emFaixa} onChange={(_e, v) => onChange({ ...editor, faixa: v })} />}
                label="Criar vários de uma vez (faixa)"
              />
            )}

            {/* Mesa/Comanda/Totem — campo "Número" único (público e faixa não usam). */}
            {!publico && !emFaixa && (
              <TextField
                label={
                  tipo === 'TOTEM' || tipo === 'TOTEM_CHECKOUT'
                    ? 'Identificação do aparelho'
                    : tipo === 'TABLET'
                      ? 'Nome do acesso'
                      : 'Número'
                }
                value={editor.numero}
                onChange={(e) => onChange({ ...editor, numero: e.target.value })}
                fullWidth
                placeholder={
                  tipo === 'TOTEM'
                    ? 'Ex.: Totem 1'
                    : tipo === 'TOTEM_CHECKOUT'
                      ? 'Ex.: Checkout 1'
                      : tipo === 'TABLET'
                        ? 'Ex.: Salão'
                        : 'Ex.: 12'
                }
                helperText={
                  tipo === 'TOTEM' || tipo === 'TOTEM_CHECKOUT'
                    ? 'Nome do equipamento, pra você saber de qual aparelho veio o pedido.'
                    : tipo === 'TABLET'
                      ? 'Um nome pra identificar este acesso — gera um link amigável (ex.: salao-3f9k).'
                      : 'Número físico da mesa/comanda. Aparece no painel do salão.'
                }
              />
            )}

            {/* Faixa de MESA/COMANDA — cria de "De" até "Até" (limite 50). */}
            {emFaixa && (
              <Box>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="De"
                    type="number"
                    value={editor.numeroInicial}
                    onChange={(e) => onChange({ ...editor, numeroInicial: e.target.value.replace(/[^0-9]/g, '') })}
                    fullWidth
                    placeholder="Ex.: 1"
                    inputProps={{ min: 0 }}
                  />
                  <TextField
                    label="Até"
                    type="number"
                    value={editor.numeroFinal}
                    onChange={(e) => onChange({ ...editor, numeroFinal: e.target.value.replace(/[^0-9]/g, '') })}
                    fullWidth
                    placeholder="Ex.: 20"
                    inputProps={{ min: 0 }}
                  />
                </Stack>
                <Typography
                  variant="caption"
                  color={totalFaixa > 50 ? 'error' : 'text.secondary'}
                  sx={{ display: 'block', mt: 0.75 }}
                >
                  {totalFaixa <= 0
                    ? 'Informe o intervalo (ex.: de 1 até 20). Máximo de 50 por faixa.'
                    : totalFaixa > 50
                      ? `${totalFaixa} acessos — máximo de 50 por faixa.`
                      : `Serão criados até ${totalFaixa} ${tipo === 'MESA' ? 'mesa(s)' : 'comanda(s)'} (${iniFaixa} a ${fimFaixa}). Números já existentes são ignorados${
                          tipo === 'COMANDA' ? ', e comandas que não existem no Sischef são puladas' : ''
                        }.`}
                </Typography>
              </Box>
            )}

            {/* Mesa/Comanda/Totem em edição — mostra o UUID como readonly. */}
            {!publico && ehEditar && (
              <TextField
                label={tipo === 'TABLET' ? 'Link do acesso (código)' : 'Código (UUID)'}
                value={editor.codigo}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText={
                  ehDispositivo
                    ? 'Gerado automaticamente. Abra esta URL no navegador do aparelho (modo kiosk) — ela é a tela inicial do dispositivo.'
                    : 'Gerado automaticamente. Usado na URL do QR code.'
                }
                sx={{ '& input': { fontFamily: 'monospace', fontSize: 13 } }}
              />
            )}

            {/* Delivery/Vitrine — subdomínio (slug) OU domínio completo. */}
            {publico && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editor.dominioCompleto}
                      onChange={(_e, v) => onChange({ ...editor, dominioCompleto: v })}
                    />
                  }
                  label="É um domínio completo (ex.: cardapiox.com.br)"
                />
                <TextField
                  label={editor.dominioCompleto ? 'Domínio' : 'Subdomínio'}
                  value={editor.codigo}
                  onChange={(e) =>
                    onChange({
                      ...editor,
                      codigo: editor.dominioCompleto
                        ? e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, '')
                        : e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                    })
                  }
                  fullWidth
                  required
                  placeholder={editor.dominioCompleto ? 'cardapiox.com.br' : 'boteco'}
                  helperText={
                    editor.dominioCompleto
                      ? 'Domínio próprio apontado pra cá (sem http:// e sem barra). Para "www", crie outro ponto de acesso.'
                      : `Slug minúsculo, só letras/números/hífen (ex.: "boteco" → boteco.${dominioCanonico()}).`
                  }
                />
              </>
            )}

            {erro && <Alert severity="error">{erro}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancelar} disabled={salvando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={onConfirmar} disabled={!podeSalvar || salvando}>
          {salvando ? <CircularProgress size={20} /> : ehEditar ? 'Salvar' : emFaixa ? 'Criar faixa' : 'Criar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
